import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { logger } from '../../utils/logger.js'
import { openBetaSearchService, type ClimbFilterOptions } from './openbeta-search-service.js'
import { YDS_MAX, V_MAX } from './openbeta-grades.js'

const log = logger.create('openbeta-layers')

const SOURCE_ID = 'openbeta-crags'
const POINTS_LAYER = 'openbeta-crags-points'
const LABELS_LAYER = 'openbeta-crags-labels'
const CLUSTER_LAYER = 'openbeta-crags-clusters'
const CLUSTER_COUNT_LAYER = 'openbeta-crags-cluster-count'
const GROUP_ID = 'climbing-crags'

const COLOR = '#e65100'
const MIN_ZOOM = 8
const CLUSTER_MAX_ZOOM = 11
const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

function registerCragIcon(map: maplibregl.Map): void {
  const imageId = 'openbeta-crag-icon'
  if (map.hasImage(imageId)) return

  const dpr = window.devicePixelRatio || 1
  const size = 28
  const canvas = document.createElement('canvas')
  canvas.width = size * dpr
  canvas.height = size * dpr
  const ctx = canvas.getContext('2d')!
  ctx.scale(dpr, dpr)

  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 2

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = COLOR
  ctx.fill()
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.save()
  ctx.translate(cx, cy)
  ctx.strokeStyle = '#ffffff'
  ctx.fillStyle = '#ffffff'
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  ctx.beginPath()
  ctx.lineWidth = 1
  ctx.moveTo(2, -9)
  ctx.lineTo(2, 8)
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(-1, -5, 2, 0, Math.PI * 2)
  ctx.fill()

  ctx.beginPath()
  ctx.lineWidth = 1.6
  ctx.moveTo(-1, -3)
  ctx.lineTo(0, 3)
  ctx.stroke()

  ctx.beginPath()
  ctx.lineWidth = 1.4
  ctx.moveTo(-1, -2)
  ctx.lineTo(2, -6)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(-1, 0)
  ctx.lineTo(2, -1)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(0, 3)
  ctx.lineTo(-3, 5)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(-3, 5)
  ctx.lineTo(1, 7)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(0, 3)
  ctx.lineTo(-2, 7)
  ctx.stroke()

  ctx.restore()

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  map.addImage(imageId, imageData, { pixelRatio: dpr })
}

class OpenBetaLayerService {
  private map: maplibregl.Map | null = null
  private enabled = false
  private dataLoaded = false

  private _showLabels = true
  private _typeMask = 0
  private _ydsMin = 0
  private _ydsMax = YDS_MAX
  private _vMin = 0
  private _vMax = V_MAX
  private _opacity = 1

  get isEnabled(): boolean {
    return this.enabled
  }
  get showLabels(): boolean {
    return this._showLabels
  }
  get typeMask(): number {
    return this._typeMask
  }
  get ydsMin(): number {
    return this._ydsMin
  }
  get ydsMax(): number {
    return this._ydsMax
  }
  get vMin(): number {
    return this._vMin
  }
  get vMax(): number {
    return this._vMax
  }
  get opacity(): number {
    return this._opacity
  }

  get isFilterActive(): boolean {
    return (
      this._typeMask !== 0 ||
      this._ydsMin !== 0 ||
      this._ydsMax !== YDS_MAX ||
      this._vMin !== 0 ||
      this._vMax !== V_MAX
    )
  }

  register(map: maplibregl.Map): void {
    this.map = map
    registerCragIcon(map)
    this.setupClickHandlers()
  }

  unregister(): void {
    if (this.enabled) this.disable()
    this.map = null
    this.dataLoaded = false
  }

  enable(): void {
    if (!this.map || this.enabled) return
    this.enabled = true
    this.addLayers()
    if (!this.dataLoaded) this.loadAllData()
  }

  disable(): void {
    if (!this.map) return
    this.enabled = false
    this.removeLayers()
  }

  toggle(): void {
    if (this.enabled) this.disable()
    else this.enable()
  }

  reAdd(): void {
    if (!this.map || !this.enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.addLayers()
      if (this.dataLoaded) this.refreshData()
      else this.loadAllData()
    }
  }

  setShowLabels(show: boolean): void {
    this._showLabels = show
    if (!this.map || !this.enabled) return
    if (show) {
      this.addLabelsLayer()
    } else {
      if (this.map.getLayer(LABELS_LAYER)) this.map.removeLayer(LABELS_LAYER)
    }
  }

  setTypeMask(mask: number): void {
    this._typeMask = mask
    this.refreshData()
  }

  setYdsRange(min: number, max: number): void {
    this._ydsMin = min
    this._ydsMax = max
    this.refreshData()
  }

  setVRange(min: number, max: number): void {
    this._vMin = min
    this._vMax = max
    this.refreshData()
  }

  setOpacity(value: number): void {
    this._opacity = value
    if (!this.map || !this.enabled) return
    const clusterOpacity = 0.7 * value
    if (this.map.getLayer(CLUSTER_LAYER))
      this.map.setPaintProperty(CLUSTER_LAYER, 'circle-opacity', clusterOpacity)
    if (this.map.getLayer(CLUSTER_COUNT_LAYER))
      this.map.setPaintProperty(CLUSTER_COUNT_LAYER, 'text-opacity', value)
    if (this.map.getLayer(POINTS_LAYER))
      this.map.setPaintProperty(POINTS_LAYER, 'icon-opacity', value)
    if (this.map.getLayer(LABELS_LAYER))
      this.map.setPaintProperty(LABELS_LAYER, 'text-opacity', value)
  }

  applyFilterState(state: {
    typeMask?: number
    ydsMin?: number
    ydsMax?: number
    vMin?: number
    vMax?: number
    showLabels?: boolean
    opacity?: number
  }): void {
    if (state.typeMask !== undefined) this._typeMask = state.typeMask
    if (state.ydsMin !== undefined) this._ydsMin = state.ydsMin
    if (state.ydsMax !== undefined) this._ydsMax = state.ydsMax
    if (state.vMin !== undefined) this._vMin = state.vMin
    if (state.vMax !== undefined) this._vMax = state.vMax
    if (state.showLabels !== undefined) this._showLabels = state.showLabels
    if (state.opacity !== undefined) this._opacity = state.opacity
  }

  private addLayers(): void {
    if (!this.map) return

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: EMPTY_GEOJSON,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: 50,
      })
    }

    if (!this.map.getLayer(CLUSTER_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLUSTER_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          minzoom: MIN_ZOOM,
          paint: {
            'circle-color': COLOR,
            'circle-opacity': 0.7,
            'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 25],
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(CLUSTER_COUNT_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: CLUSTER_COUNT_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          minzoom: MIN_ZOOM,
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 12,
          },
          paint: {
            'text-color': '#ffffff',
          },
        },
        GROUP_ID
      )
    }

    if (!this.map.getLayer(POINTS_LAYER)) {
      addOverlayLayer(
        this.map,
        {
          id: POINTS_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          minzoom: MIN_ZOOM,
          layout: {
            'icon-image': 'openbeta-crag-icon',
            'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 12, 0.8, 14, 1],
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
          },
        },
        GROUP_ID
      )
    }

    if (this._showLabels) this.addLabelsLayer()
  }

  private addLabelsLayer(): void {
    if (!this.map || this.map.getLayer(LABELS_LAYER)) return
    addOverlayLayer(
      this.map,
      {
        id: LABELS_LAYER,
        type: 'symbol',
        source: SOURCE_ID,
        filter: ['!', ['has', 'point_count']],
        minzoom: MIN_ZOOM,
        layout: {
          'text-field': ['get', 'areaName'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-max-width': 8,
          'text-optional': true,
        },
        paint: {
          'text-color': '#1e293b',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      },
      GROUP_ID
    )
  }

  private removeLayers(): void {
    if (!this.map) return
    for (const layerId of [LABELS_LAYER, POINTS_LAYER, CLUSTER_COUNT_LAYER, CLUSTER_LAYER]) {
      if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }

  /** Layer ids that respond to identify / cluster-zoom (for the hover cursor). */
  interactiveLayerIds(): string[] {
    return [POINTS_LAYER, CLUSTER_LAYER]
  }

  queryFeaturesAtPoint(point: maplibregl.Point): Array<{
    uuid: string
    name: string
    totalClimbs: number
    disciplines: Record<string, number>
  }> {
    if (!this.map || !this.enabled) return []
    const hits: Array<{
      uuid: string
      name: string
      totalClimbs: number
      disciplines: Record<string, number>
    }> = []
    const features = this.map.queryRenderedFeatures(point, { layers: [POINTS_LAYER] })
    for (const f of features) {
      const p = f.properties
      if (!p?.uuid) continue
      let disc: Record<string, number> = {}
      try {
        disc = JSON.parse(p.disciplines ?? '{}')
      } catch {
        /* ignore */
      }
      hits.push({
        uuid: p.uuid,
        name: p.areaName ?? '',
        totalClimbs: Number(p.totalClimbs) || 0,
        disciplines: disc,
      })
    }
    return hits
  }

  private setupClickHandlers(): void {
    if (!this.map) return

    this.map.on('click', CLUSTER_LAYER, (e: maplibregl.MapLayerMouseEvent) => {
      if (!this.map || !e.features?.length) return
      const clusterId = e.features[0].properties?.cluster_id as number | undefined
      if (clusterId == null) return
      const source = this.map.getSource<maplibregl.GeoJSONSource>(SOURCE_ID)
      if (!source) return
      source
        .getClusterExpansionZoom(clusterId)
        .then(zoom => {
          this.map?.easeTo({ center: e.lngLat, zoom: zoom + 0.5 })
        })
        .catch(() => {})
    })

    for (const layer of [CLUSTER_LAYER, POINTS_LAYER]) {
      this.map.on('mouseenter', layer, () => {
        if (this.map) this.map.getCanvas().style.cursor = 'pointer'
      })
      this.map.on('mouseleave', layer, () => {
        if (this.map) this.map.getCanvas().style.cursor = ''
      })
    }
  }

  private loadAllData(): void {
    openBetaSearchService
      .ensureLoaded()
      .then(() => {
        this.dataLoaded = true
        this.refreshData()
      })
      .catch(err => {
        log.warn('Failed to load OpenBeta index for map overlay', err)
      })
  }

  private refreshData(): void {
    if (!this.map || !this.enabled) return
    const source = this.map.getSource<maplibregl.GeoJSONSource>(SOURCE_ID)
    if (!source) return

    let areas = openBetaSearchService.queryWithin([-180, -90, 180, 90])

    if (this.isFilterActive) {
      const filter: ClimbFilterOptions = {
        typeMask: this._typeMask,
        ydsMin: this._ydsMin,
        ydsMax: this._ydsMax,
        vMin: this._vMin,
        vMax: this._vMax,
      }
      const matching = openBetaSearchService.getMatchingAreaUuids(filter)
      if (matching) {
        areas = areas.filter(a => matching.has(a.u))
      }
    }

    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: areas.map(a => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [a.c[1], a.c[0]],
        },
        properties: {
          uuid: a.u,
          areaName: a.n,
          totalClimbs: a.t,
          pathTokens: JSON.stringify(a.p),
          disciplines: JSON.stringify(a.d ?? {}),
        },
      })),
    }
    source.setData(fc)
    log.info(`Map overlay: ${areas.length} areas`)
  }
}

export const openBetaLayerService = new OpenBetaLayerService()
